import { useCallback, useEffect, useState } from 'react'

import { api, ApiError } from './api'

export type PushPhase = 'unsupported' | 'denied' | 'off' | 'on' | 'busy'

/** Must match CACHE in sw.js; anything else means a stale worker is running. */
export const EXPECTED_SW = 'acecast-shell-v4'

async function readSwVersion(): Promise<string | null> {
  try {
    const registration = await navigator.serviceWorker.ready
    // Ask the browser to re-check sw.js now rather than on its own schedule.
    void registration.update().catch(() => {})
    const worker = registration.active
    if (!worker) return null
    return await new Promise<string | null>((resolve) => {
      const channel = new MessageChannel()
      channel.port1.onmessage = (event) => resolve(String(event.data))
      worker.postMessage('version', [channel.port2])
      window.setTimeout(() => resolve(null), 2000)
    })
  } catch {
    return null
  }
}

/**
 * The applicationServerKey has to be raw bytes, not the base64url the API
 * returns. Build it over a concrete ArrayBuffer: Uint8Array.from yields
 * Uint8Array<ArrayBufferLike>, which no longer satisfies BufferSource.
 */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=')
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export function usePush() {
  const [phase, setPhase] = useState<PushPhase>('busy')
  const [error, setError] = useState<string | null>(null)
  const [swVersion, setSwVersion] = useState<string | null>(null)

  const sync = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPhase('unsupported')
      return
    }
    if (Notification.permission === 'denied') {
      setPhase('denied')
      return
    }
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      setPhase(subscription ? 'on' : 'off')
      setSwVersion(await readSwVersion())
    } catch {
      setPhase('unsupported')
    }
  }, [])

  useEffect(() => {
    void sync()
  }, [sync])

  const enable = useCallback(async () => {
    setPhase('busy')
    setError(null)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setPhase(permission === 'denied' ? 'denied' : 'off')
        return
      }
      const { publicKey } = await api.getPushKey()
      if (!publicKey) throw new Error('El servidor no té claus de notificació')

      const registration = await navigator.serviceWorker.ready
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeKey(publicKey),
        }))

      await api.pushSubscribe(subscription.toJSON())
      setPhase('on')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
      void sync()
    }
  }, [sync])

  const disable = useCallback(async () => {
    setPhase('busy')
    setError(null)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await api.pushUnsubscribe(subscription.endpoint).catch(() => {})
        await subscription.unsubscribe()
      }
      setPhase('off')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
      void sync()
    }
  }, [sync])

  return { phase, error, enable, disable, swVersion, swStale: swVersion !== null && swVersion !== EXPECTED_SW }
}
